/**
 * Rutas de las landings del catálogo genérico.
 *
 * Fichero aparte del registro de contenido (`./index.ts`) a propósito: el
 * switcher de idioma y la página de acceso necesitan los segmentos en el
 * bundle cliente, y un import de este módulo no debe arrastrar el editorial
 * de las landings. Mismo criterio que las guías, cuyos slugs tampoco se
 * importan desde `lib/locale-paths`.
 */

import { getLocalePrefix, hrefForSegment, toPublicLocale, type PublicLocale } from '@/lib/locale-prefix'
import { GENERIC_LANDINGS_PUBLISHED } from '@/lib/locale-capabilities'
import type { GenericLandingKey } from './types'

/** Segmento de cada landing por locale (sin barra inicial). */
export const CONEJO_PASCUA_PATH_BY_LOCALE: Record<PublicLocale, string> = {
  es: 'conejo-de-pascua',
  en: 'easter-bunny',
  pt: 'coelho-da-pascoa',
  it: 'coniglietto-di-pasqua',
  fr: 'lapin-de-paques',
  ja: 'easter-bunny',
  de: 'osterhase',
}

/** Halloween se llama igual en todos los mercados publicados. */
export const HALLOWEEN_PATH_BY_LOCALE: Record<PublicLocale, string> = {
  es: 'halloween',
  en: 'halloween',
  pt: 'halloween',
  it: 'halloween',
  fr: 'halloween',
  ja: 'halloween',
  de: 'halloween',
}

export const MUNDO_MAGICO_PATH_BY_LOCALE: Record<PublicLocale, string> = {
  es: 'mundo-magico',
  en: 'magic-world',
  pt: 'mundo-magico',
  it: 'mondo-magico',
  fr: 'monde-magique',
  ja: 'mahou-no-sekai',
  de: 'magische-welt',
}

export const CUMPLEANOS_PATH_BY_LOCALE: Record<PublicLocale, string> = {
  es: 'cumpleanos',
  en: 'birthday',
  pt: 'aniversario',
  it: 'compleanno',
  fr: 'anniversaire',
  ja: 'tanjoubi',
  de: 'geburtstag',
}

/** Locales con landings del catálogo publicadas.
 *  Fuente única: el registro de capacidades (`lib/locale-capabilities`). */
export const LANDING_LOCALES = GENERIC_LANDINGS_PUBLISHED

/** Home del locale: destino seguro cuando la landing no está publicada en él. */
function localeHome(locale: PublicLocale): string {
  return getLocalePrefix(locale) || '/'
}

/**
 * Href de una landing en el locale pedido: publicado → su segmento; público
 * sin contenido aún → home del locale, nunca un enlace que acaba en 404.
 * La fuente de verdad de qué locales están publicados es `LANDING_LOCALES`.
 * Añadir un idioma requiere incorporarlo aquí y aportar el registro editorial
 * y las rutas Next correspondientes; el test del registro impide dejar una
 * familia publicada sin contenido completo.
 */
function catalogHref(byLocale: Record<PublicLocale, string>, locale: PublicLocale): string {
  if (!(LANDING_LOCALES as readonly string[]).includes(locale)) {
    return localeHome(locale)
  }
  return hrefForSegment(byLocale, locale)
}


/**
 * Segmento conocido en cualquier idioma → href en el locale pedido. Es la
 * fuente única del enlazado entre landings, del switcher de idioma (vía
 * `LANDING_RESOLVERS` en lib/locale-paths) y del back-link de la página de
 * acceso compartida. Qué locales están publicados lo declara
 * `LANDING_LOCALES`; el gating lo aplica el switcher.
 */
export const GENERIC_LANDING_RESOLVERS: Array<{
  segments: string[]
  href: (locale: PublicLocale) => string
}> = [
  { segments: Object.values(CONEJO_PASCUA_PATH_BY_LOCALE), href: getConejoPascuaHref },
  { segments: Object.values(HALLOWEEN_PATH_BY_LOCALE), href: getHalloweenHref },
  { segments: Object.values(MUNDO_MAGICO_PATH_BY_LOCALE), href: getMundoMagicoHref },
  { segments: Object.values(CUMPLEANOS_PATH_BY_LOCALE), href: getCumpleanosHref },
]

const SEGMENTS_BY_KEY: Record<GenericLandingKey, Record<PublicLocale, string>> = {
  'conejo-de-pascua': CONEJO_PASCUA_PATH_BY_LOCALE,
  halloween: HALLOWEEN_PATH_BY_LOCALE,
  'mundo-magico': MUNDO_MAGICO_PATH_BY_LOCALE,
  cumpleanos: CUMPLEANOS_PATH_BY_LOCALE,
}

/** Href de una landing del catálogo por su clave de registro. */
export function getGenericLandingHref(key: GenericLandingKey, locale: string): string {
  return catalogHref(SEGMENTS_BY_KEY[key], toPublicLocale(locale))
}

/**
 * Segmento (sin prefijo de locale) de una landing. Lo usan los CTA para
 * decirle a `/auth-mundo-magico` de dónde viene el usuario (`?from=`).
 */
export function getGenericLandingSegment(key: GenericLandingKey, locale: string): string {
  const byLocale = SEGMENTS_BY_KEY[key]
  return byLocale[toPublicLocale(locale)] ?? byLocale.es
}

/** Href de la landing del Conejo de Pascua (segmento localizado). */
export function getConejoPascuaHref(locale: PublicLocale): string {
  return catalogHref(CONEJO_PASCUA_PATH_BY_LOCALE, locale)
}

/** Href de la landing de Halloween. */
export function getHalloweenHref(locale: PublicLocale): string {
  return catalogHref(HALLOWEEN_PATH_BY_LOCALE, locale)
}

/** Href del hub Mundo Mágico (segmento localizado). */
export function getMundoMagicoHref(locale: PublicLocale): string {
  return catalogHref(MUNDO_MAGICO_PATH_BY_LOCALE, locale)
}

/** Href de la landing de cumpleaños (segmento localizado). */
export function getCumpleanosHref(locale: PublicLocale): string {
  return catalogHref(CUMPLEANOS_PATH_BY_LOCALE, locale)
}

/**
 * Resuelve el back-link de `/auth-mundo-magico` desde su `?from=<segmento>`.
 * Set cerrado a propósito: un segmento desconocido devuelve `undefined` (la
 * página cae al hub) en lugar de construir una ruta con input del usuario.
 */
export function getGenericLandingHrefBySegment(
  segment: string | undefined,
  locale: string
): string | undefined {
  if (!segment) return undefined
  return GENERIC_LANDING_RESOLVERS.find((resolver) => resolver.segments.includes(segment))?.href(
    toPublicLocale(locale)
  )
}
